/**
 * Query layer. One `/bootstrap` per stored key — Jeff holds two, everyone else one — merged
 * into a single view, with the key that granted each group carried along so writes know which
 * link to present.
 */
import {
  useMutation,
  useQueries,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { sortSessions, todayIso } from '@topspin/shared';
import type {
  Availability,
  BootstrapResponse,
  CalendarSubscriptionSummary,
  Fill,
  GroupConfig,
  Override,
  Session,
} from '@topspin/shared';
import {
  ApiError,
  createFill,
  createOut,
  deleteOut,
  fetchBootstrap,
  mintCalendarSubscription,
  setAvailability,
  stopCalendarSubscription,
  updateOut,
  type AvailabilityInput,
  type CalendarSubscriptionMinted,
  type FillInput,
  type OutInput,
} from './api.js';
import { clearCalendarUrl, getKeys, getPlayerId, setCalendarUrl } from './storage.js';

export interface GroupView {
  key: string;
  /** True when {@link key} is the admin's own group ID, and so opens the admin-only reads (§5). */
  admin: boolean;
  config: GroupConfig;
  sessions: Session[];
  overrides: Override[];
  availabilities: Availability[];
  fills: Fill[];
}

export interface SeasonView {
  seasonId: string;
  serverDate: string;
  groups: GroupView[];
  /** The player's one live calendar subscription, whichever device minted it. */
  calendarSubscription?: CalendarSubscriptionSummary;
}

export const bootstrapKey = (key: string) => ['bootstrap', key] as const;

/**
 * One `SeasonView` from the responses to `keys`, positionally: `responses[i]` answered
 * `keys[i]`, whether or not it has landed yet, so the group carries the group ID that
 * granted it and writes present the right link.
 *
 * Groups are deduplicated by group ID. Both groups' bootstraps are the same URL, so a
 * service worker from a build before the per-group cache key can still answer two different
 * group IDs out of one cached response — which showed up as one group's whole season listed
 * twice. Keeping the first answer for a group ID is what an old cache degrades to now.
 */
export function mergeSeasonView(
  keys: string[],
  responses: Array<BootstrapResponse | undefined>,
): SeasonView | undefined {
  const first = responses.find((r) => r);
  if (!first) return undefined;

  const byGroupId = new Map<string, GroupView>();
  const groups: GroupView[] = [];
  responses.forEach((response, index) => {
    const admin = response?.admin === true;
    for (const g of response?.groups ?? []) {
      const kept = byGroupId.get(g.config.groupId);
      if (kept) {
        // A device accumulates group IDs, so the admin's holds his own ID *and* the one the
        // players got, in whatever order he happened to type them. The first answer still
        // supplies the schedule — but if a later one came from the admin ID, carry its key,
        // or the admin-only reads would go out under whichever ID landed first and 403 (§5).
        if (admin && !kept.admin) {
          kept.key = keys[index] as string;
          kept.admin = true;
        }
        continue;
      }
      const view: GroupView = {
        key: keys[index] as string,
        admin,
        config: g.config,
        sessions: sortSessions(g.sessions),
        overrides: g.overrides,
        // A PWA installed before availability answers existed can still be holding a
        // cached bootstrap without the field. Same for fills, added later still.
        availabilities: g.availabilities ?? [],
        fills: g.fills ?? [],
      };
      byGroupId.set(g.config.groupId, view);
      groups.push(view);
    }
  });

  // One per player, so every response carries the same answer — but a PWA holding a cached
  // bootstrap from before this field existed carries none, so take the first that has one.
  const calendarSubscription = responses.find((r) => r?.calendarSubscription)
    ?.calendarSubscription;

  return {
    seasonId: first.seasonId,
    serverDate: first.serverDate,
    groups,
    ...(calendarSubscription ? { calendarSubscription } : {}),
  };
}

/**
 * True while a stored key still has an answer on its way and nothing to show for it. Every key
 * has to land before a screen renders: letting them through one at a time painted the first
 * group's schedule and then shoved it down when the second one arrived a moment later.
 *
 * A query whose retries are paused is the exception — offline, it has no answer coming until
 * the network is back, so waiting on it would hold the whole app on the loading line. Those
 * fall through to whatever the persisted cache holds, which is the point of a cold launch at
 * the courts (§7).
 */
export function isSeasonPending(
  results: Array<{ isPending: boolean; fetchStatus: string }>,
): boolean {
  return results.some((r) => r.isPending && r.fetchStatus !== 'paused');
}

export function useBootstrap(keys: string[]): {
  data: SeasonView | undefined;
  results: Array<UseQueryResult<BootstrapResponse, Error>>;
  isPending: boolean;
  unauthorized: boolean;
} {
  const playerId = getPlayerId();
  const results = useQueries({
    queries: keys.map((key) => ({
      queryKey: bootstrapKey(key),
      queryFn: () => fetchBootstrap(key, playerId),
      // The schedule changes rarely; a stale-while-revalidate window keeps launches instant.
      staleTime: 60_000,
      retry: (count: number, error: Error) =>
        !(error instanceof ApiError && error.status === 401) && count < 2,
    })),
  }) as Array<UseQueryResult<BootstrapResponse, Error>>;

  const data = mergeSeasonView(
    keys,
    results.map((r) => r.data),
  );

  return {
    data,
    results,
    isPending: isSeasonPending(results),
    unauthorized:
      keys.length > 0 &&
      results.every((r) => r.error instanceof ApiError && r.error.status === 401),
  };
}

/** Server date when we have it; the device clock is the offline fallback. */
export function today(view: SeasonView | undefined): string {
  return view?.serverDate ?? todayIso();
}

export function findGroup(view: SeasonView | undefined, groupId: string): GroupView | undefined {
  return view?.groups.find((g) => g.config.groupId === groupId);
}

function useInvalidate() {
  const client = useQueryClient();
  return (key: string) => client.invalidateQueries({ queryKey: bootstrapKey(key) });
}

export function useCreateOut() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (vars: { key: string; input: OutInput }) =>
      createOut(vars.key, getPlayerId(), vars.input),
    onSuccess: (_data, vars) => invalidate(vars.key),
  });
}

export function useUpdateOut() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (vars: { key: string; input: OutInput & { expectedUpdatedAt: string } }) =>
      updateOut(vars.key, getPlayerId(), vars.input),
    onSuccess: (_data, vars) => invalidate(vars.key),
  });
}

export function useDeleteOut() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (vars: {
      key: string;
      input: { seasonId: string; groupId: string; date: string; outPlayerId: string };
    }) => deleteOut(vars.key, getPlayerId(), vars.input),
    onSuccess: (_data, vars) => invalidate(vars.key),
  });
}

export function useCreateFill() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (vars: { key: string; input: FillInput }) =>
      createFill(vars.key, getPlayerId(), vars.input),
    onSuccess: (_data, vars) => invalidate(vars.key),
  });
}

export function useSetAvailability() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (vars: { key: string; input: AvailabilityInput }) =>
      setAvailability(vars.key, getPlayerId(), vars.input),
    onSuccess: (_data, vars) => invalidate(vars.key),
  });
}

/**
 * Mint or re-mint this player's calendar link. Every stored key goes in the body so a two-group
 * player comes away with one calendar covering both nights; the URL is kept on the device
 * because the server holds only its hash.
 */
export function useMintCalendar() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (vars: { key: string }): Promise<CalendarSubscriptionMinted> =>
      mintCalendarSubscription(vars.key, getPlayerId(), getKeys()),
    onSuccess: (data) => {
      setCalendarUrl(data.url);
      // Every key's bootstrap carries the subscription, so they all go stale at once.
      void client.invalidateQueries({ queryKey: ['bootstrap'] });
    },
  });
}

export function useStopCalendar() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (vars: { key: string }) => stopCalendarSubscription(vars.key, getPlayerId()),
    onSuccess: () => {
      clearCalendarUrl();
      void client.invalidateQueries({ queryKey: ['bootstrap'] });
    },
  });
}

export function useStoredKeys(): string[] {
  return getKeys();
}
